import React, { createContext, useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer, ToastPosition, Theme } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/components/NotificationProvider.css';
import Icon from './Icon';
import { useAuth } from '../contexts/AuthContext';
import { loadServerNotifications, markServerNotificationRead, markAllServerNotificationsRead, ServerNotification } from '../services/NotificationService';

interface NotificationItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  timestamp: Date;
  read: boolean;
  persistent?: boolean;
  /** Set on a server-backed notification (currently just @mentions) — its id
   *  is the real notifications.id on ans-backend, and read-state round-trips
   *  through markServerNotificationRead/markAllServerNotificationsRead
   *  instead of only ever living in this tab's local state. */
  serverId?: string;
  entityType?: string | null;
  entityId?: string | null;
}

// Poll interval for server notifications — a mention from someone else's
// browser can't push into this tab, so this is what actually surfaces it
// (short of building a websocket channel this app doesn't have yet).
const POLL_INTERVAL_MS = 60_000;

interface NotificationContextType {
  showSuccess: (message: string, options?: any) => void;
  showError: (message: string, options?: any) => void;
  showInfo: (message: string, options?: any) => void;
  showWarning: (message: string, options?: any) => void;
  showNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning', options?: any) => void;
  notifications: NotificationItem[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Custom toast styles that match your app's design
const toastConfig = {
  position: 'top-right' as ToastPosition,
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: 'light' as Theme,
  style: {
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid var(--border-color, #e5e7eb)',
    fontFamily: 'inherit',
  },
};

/** The same message is shown at most once per minute, so a failing poll cannot stack identical toasts. */
export const TOAST_DEDUPE_MS = 60_000;
const lastShown = new Map<string, number>();
export function shouldShowToast(type: string, message: string, now: number = Date.now()): boolean {
  const key = `${type}:${message}`;
  const prev = lastShown.get(key);
  if (prev !== undefined && now - prev < TOAST_DEDUPE_MS) return false;
  lastShown.set(key, now);
  if (lastShown.size > 200) {
    for (const [k, t] of lastShown) if (now - t >= TOAST_DEDUPE_MS) lastShown.delete(k);
  }
  return true;
}
export function resetToastDedupe(): void {
  lastShown.clear();
}

const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [serverNotifications, setServerNotifications] = useState<ServerNotification[]>([]);

  // Load local (ephemeral toast) notifications from localStorage on mount —
  // unrelated to the server ones below, this is still purely per-tab.
  useEffect(() => {
    const saved = localStorage.getItem('app-notifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const notificationsWithDates: NotificationItem[] = parsed.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
        setNotifications(notificationsWithDates);
      } catch (error) {
        console.error('Error loading notifications:', error);
      }
    }
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('app-notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Server notifications (currently just @mentions) — fetched once signed
  // in, then polled. A mention from someone else's browser has no way to
  // push into this tab, so polling is what actually surfaces it.
  const refreshServerNotifications = useCallback(async () => {
    if (!isAuthenticated || !user?.email) return;
    try {
      const { notifications: fetched } = await loadServerNotifications();
      setServerNotifications(fetched);
    } catch (error) {
      console.error('Error loading server notifications:', error);
    }
  }, [isAuthenticated, user?.email]);

  useEffect(() => {
    void refreshServerNotifications();
    if (!isAuthenticated) return;
    const interval = setInterval(() => void refreshServerNotifications(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshServerNotifications]);

  // What consumers (the bell dropdown) actually read — local ephemeral
  // toasts and server-backed mentions merged into one list, newest first.
  const mergedNotifications = useMemo<NotificationItem[]>(() => {
    const fromServer: NotificationItem[] = serverNotifications.map(n => ({
      id: `server-${n.id}`,
      serverId: n.id,
      message: n.message,
      type: 'info',
      timestamp: new Date(n.created_at),
      read: n.read,
      persistent: true,
      entityType: n.entity_type,
      entityId: n.entity_id,
    }));
    return [...fromServer, ...notifications].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [serverNotifications, notifications]);

  const unreadCount = useMemo(
    () => mergedNotifications.filter(n => !n.read).length,
    [mergedNotifications]
  );

  const addNotification = useCallback((notification: NotificationItem) => {
    setNotifications(prev => [notification, ...prev].slice(0, 50)); // Keep only last 50
  }, []);



  const markAsRead = useCallback((id: string) => {
    const serverId = id.startsWith('server-') ? id.slice('server-'.length) : null;
    if (serverId) {
      setServerNotifications(prev => prev.map(n => n.id === serverId ? { ...n, read: true } : n));
      markServerNotificationRead(serverId).catch(err => console.error('Error marking notification read:', err));
      return;
    }
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
    if (serverNotifications.some(n => !n.read)) {
      setServerNotifications(prev => prev.map(n => ({ ...n, read: true })));
      markAllServerNotificationsRead().catch(err => console.error('Error marking notifications read:', err));
    }
  }, [serverNotifications]);

  const clearNotification = useCallback((id: string) => {
    // No delete endpoint for a server-backed notification — closest
    // equivalent available is marking it read, same as dismissing a toast
    // just stops it demanding attention rather than erasing the event.
    if (id.startsWith('server-')) {
      markAsRead(id);
      return;
    }
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, [markAsRead]);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);
  const showNotification = useCallback((
    message: string,
    type: 'success' | 'error' | 'info' | 'warning',
    options: any = {}
  ) => {
    if (!shouldShowToast(type, message)) return undefined;
    const defaultOptions = {
      ...toastConfig,
      ...options,
    };

    // Add to persistent notifications if persistent option is true
    if (options.persistent) {
      const notification: NotificationItem = {
        id: `notif-${Date.now()}`,
        message,
        type,
        timestamp: new Date(),
        read: false,
        persistent: true,
      };
      addNotification(notification);
    }

    switch (type) {
      case 'success':
        return toast.success(message, {
          ...defaultOptions,
          icon: <Icon name="check" size={16} />,
          style: {
            ...defaultOptions.style,
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            border: '1px solid #86efac',
          },
        });
      case 'error':
        return toast.error(message, {
          ...defaultOptions,
          icon: <Icon name="x" size={16} />,
          style: {
            ...defaultOptions.style,
            background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
            border: '1px solid #fca5a5',
          },
        });
      case 'warning':
        return toast.warning(message, {
          ...defaultOptions,
          icon: <Icon name="warning" size={16} />,
          style: {
            ...defaultOptions.style,
            background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
            border: '1px solid #fcd34d',
          },
        });
      case 'info':
        return toast.info(message, {
          icon: <Icon name="check" size={16} />,
          style: {
            ...defaultOptions.style,
            background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
            border: '1px solid #93c5fd',
          },
        });
      default:
        return toast(message, defaultOptions);
    }
  }, [addNotification]);

  const showSuccess = useCallback((message: string, options?: any) => {
    return showNotification(message, 'success', options);
  }, [showNotification]);

  const showError = useCallback((message: string, options?: any) => {
    return showNotification(message, 'error', options);
  }, [showNotification]);

  const showInfo = useCallback((message: string, options?: any) => {
    return showNotification(message, 'info', options);
  }, [showNotification]);

  const showWarning = useCallback((message: string, options?: any) => {
    return showNotification(message, 'warning', options);
  }, [showNotification]);

  return (
    <NotificationContext.Provider
      value={{
        showSuccess,
        showError,
        showInfo,
        showWarning,
        showNotification,
        notifications: mergedNotifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearAllNotifications,
      }}
    >
      {children}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        className="custom-toast-container"
      />
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
